
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, CheckCircle, Star, Truck } from "lucide-react";
import { Department, City } from "@/components/location-bennes/LocationData";

interface DepartmentSEOPageProps {
  departmentCode: string;
  departmentName: string;
  region: string;
  mainCities: string[];
  description: string;
  heroTitle: string;
  heroSubtitle: string;
  specificServices: string[];
  localTestimonial?: {
    text: string;
    author: string;
    city: string;
  };
  nearbyDepartments: Department[];
}

const DepartmentSEOPage: React.FC<DepartmentSEOPageProps> = ({
  departmentCode,
  departmentName,
  region,
  mainCities,
  description,
  heroTitle,
  heroSubtitle,
  specificServices,
  localTestimonial,
  nearbyDepartments
}) => {
  const scrollToRequestForm = () => {
    const formElement = document.getElementById('request-form');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="space-y-12">
      {/* Hero Section géolocalisée */}
      <section className="bg-gradient-to-br from-fabre-lightgreen to-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-fabre-gray mb-4">
            {heroTitle}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            {heroSubtitle}
          </p>
          <div className="flex items-center justify-center gap-2 text-fabre-darkgreen">
            <MapPin className="h-5 w-5" />
            <span className="font-medium">
              Service dans le {departmentName} ({departmentCode}) - Région {region}
            </span>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <Truck className="h-8 w-8 text-fabre-green mx-auto mb-3" />
            <h3 className="font-semibold text-fabre-darkgreen mb-2">Livraison 24-48h</h3>
            <p className="text-sm text-gray-600">Dans tout le {departmentName}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <Phone className="h-8 w-8 text-fabre-green mx-auto mb-3" />
            <h3 className="font-semibold text-fabre-darkgreen mb-2">Devis gratuit</h3>
            <p className="text-sm text-gray-600">Estimation personnalisée</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <CheckCircle className="h-8 w-8 text-fabre-green mx-auto mb-3" />
            <h3 className="font-semibold text-fabre-darkgreen mb-2">Service local</h3>
            <p className="text-sm text-gray-600">Équipe de proximité</p>
          </div>
        </div>
        
        <div className="text-center">
          <button 
            onClick={scrollToRequestForm}
            className="bg-fabre-green text-white px-8 py-3 rounded-lg hover:bg-fabre-darkgreen transition-colors font-semibold"
          >
            Demander un devis {departmentName}
          </button>
        </div>
      </section>

      {/* Section description du département */}
      <section className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6">
          Location de bennes dans le {departmentName} - Notre expertise locale
        </h2>
        <div className="prose max-w-none text-gray-700">
          <p className="text-lg mb-6 leading-relaxed">
            {description}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold text-fabre-darkgreen mb-4">
                Principales villes desservies
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {mainCities.map((city, index) => (
                  <div key={index} className="bg-fabre-lightgreen p-3 rounded text-center">
                    <div className="font-medium text-fabre-darkgreen">{city}</div>
                    <div className="text-sm text-gray-600">Location benne</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-fabre-darkgreen mb-4">
                Services spécialisés {departmentName}
              </h3>
              <ul className="space-y-3">
                {specificServices.map((service, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-fabre-green mr-2 mt-0.5 flex-shrink-0" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section tarifs par type de benne */}
      <section className="bg-gradient-to-br from-fabre-lightgreen to-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
          Tarifs location bennes {departmentName} - Prix 2024
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-fabre-gray mb-3">Benne gravats 8m³</h3>
            <p className="text-gray-600 mb-4 text-sm">Idéale pour béton, briques, tuiles</p>
            <div className="text-2xl font-bold text-fabre-green mb-3">À partir de 120€</div>
            <p className="text-xs text-gray-500 mb-4">Livraison et collecte dans le {departmentName}</p>
            <button 
              onClick={scrollToRequestForm}
              className="w-full bg-fabre-green text-white py-2 rounded hover:bg-fabre-darkgreen transition-colors text-sm"
            >
              Commander {departmentName}
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-fabre-gray mb-3">Benne tout-venant 15m³</h3>
            <p className="text-gray-600 mb-4 text-sm">Parfaite pour encombrants, DIB</p>
            <div className="text-2xl font-bold text-fabre-green mb-3">À partir de 180€</div>
            <p className="text-xs text-gray-500 mb-4">Service rapide {departmentName}</p>
            <button 
              onClick={scrollToRequestForm}
              className="w-full bg-fabre-green text-white py-2 rounded hover:bg-fabre-darkgreen transition-colors text-sm"
            >
              Commander {departmentName}
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-fabre-gray mb-3">Benne déchets verts 20m³</h3>
            <p className="text-gray-600 mb-4 text-sm">Spécialisée jardinage, élagage</p>
            <div className="text-2xl font-bold text-fabre-green mb-3">À partir de 160€</div>
            <p className="text-xs text-gray-500 mb-4">Valorisation écologique</p>
            <button 
              onClick={scrollToRequestForm}
              className="w-full bg-fabre-green text-white py-2 rounded hover:bg-fabre-darkgreen transition-colors text-sm"
            >
              Commander {departmentName}
            </button>
          </div>
        </div>
      </section>

      {/* Témoignage local si disponible */}
      {localTestimonial && (
        <section className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
            Témoignage client {departmentName}
          </h2>
          <div className="bg-fabre-lightgreen p-6 rounded-lg max-w-3xl mx-auto">
            <div className="flex items-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-gray-700 mb-4 italic">"{localTestimonial.text}"</p>
            <div className="text-sm text-fabre-darkgreen font-medium">
              {localTestimonial.author} - {localTestimonial.city}, {departmentName}
            </div>
          </div>
        </section>
      )}

      {/* Section départements voisins */}
      <section className="bg-gray-50 rounded-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
          Autres zones de service
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Découvrez nos services de location de bennes dans les départements voisins
        </p>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {nearbyDepartments.slice(0, 8).map((dept) => (
            <Link
              key={dept.code}
              to={`/nos-services/location-bennes/${dept.code}`}
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow group"
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-fabre-green" />
                <h3 className="font-medium text-fabre-gray group-hover:text-fabre-green transition-colors">
                  {dept.name} ({dept.code})
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Location bennes {dept.name}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DepartmentSEOPage;
